import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, Category } from '@/mocks/categories';
import { getArticlesByCategory, Article } from '@/mocks/articles';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<Category | undefined>();
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    const cat = categories.find((c) => c.slug === slug);
    setActiveCategory(cat);
    if (slug) {
      setArticles(getArticlesByCategory(slug));
    }
  }, [slug]);

  if (!activeCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-500">分类不存在</p>
      </div>
    );
  }

  return (
    <>
      <SeoHead
        title={`${activeCategory.name}`}
        description={`${activeCategory.description} - 火影黄漫导航提供${activeCategory.name}相关文章${activeCategory.count}篇，帮助爱好者深入了解火影忍者成人向同人漫画。`}
        keywords={`火影黄漫,火影忍者同人漫画,${activeCategory.name},火影同人,火影成人向同人`}
        canonicalUrl={`/category/${slug}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${activeCategory.name} - 火影黄漫导航`,
          description: activeCategory.description,
          url: `https://huoying-doujin.com/category/${slug}`,
        }}
      />
      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: activeCategory.name },
            ]}
          />

          <div className="mt-6 mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              {activeCategory.name}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base">
              {activeCategory.description}
            </p>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-3 mb-10 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">({cat.count})</span>
              </Link>
            ))}
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-4xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-3 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span>{article.readTime}</span>
                      <span>{article.views.toLocaleString()} 阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
}